import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";

import { useRequestDeviceInfo } from "@asurion-core/expert-workspace-timeline";
import { useDeviceReplacement } from "@expert/sales-tools";
import {
    hasProbedSession,
    isGuideSupported,
    markSessionProbed,
    resolveDeviceActivationSupport,
    selectGaiaGuideDevice,
    sendMetadata,
    triggerNextStep,
    useDeviceInfoStore,
    useGuideSession,
    useGuideStore,
} from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import { getRootDispatcher } from "@/analytics";

/** Both the guide title and the `currentGuideName` Simplr reports for it. */
const DEVICE_ACTIVATION_GUIDE = "Device Activation";

/**
 * Whether the device we surfaced agrees with the claim replacement device, which is the only ground truth
 * we hold in-band: it's the handset actually being shipped. A `mismatch` is the closest thing we have to a
 * "we surfaced the wrong device" signal.
 *
 * Fuzzy on purpose — Horizon's make/model is free text ("iphone 14") while GAIA's is a normalized catalog
 * name ("iPhone 15 Pro"), so exact equality would report mismatches that aren't.
 */
const compareWithClaim = (surfaced: string, claim: string): "match" | "partial" | "mismatch" | "no-claim" => {
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
    const a = norm(surfaced);
    const b = norm(claim);
    // Both empty-checks are required: `includes("")` is true for every string, so an empty side would
    // otherwise report a false "partial".
    if (!a || !b) return "no-claim";
    if (a === b) return "match";
    return a.includes(b) || b.includes(a) ? "partial" : "mismatch";
};

/**
 * Puts the customer's device into the activation flow's session metadata, GAIA first and the
 * customer-details replacement device as fallback (which is also the only source of eSim/imei/iccid).
 */
export const useGuideDeviceInfo = ({
    logger,
    sessionId,
    gaiaPayloadProps,
}: {
    logger: Logger;
    sessionId: string;
    gaiaPayloadProps: GaiaPayloadConfigs;
}) => {
    const { currentGuideName, sessionMetadata, supportedDevices, unsupportedStepId } = useGuideSession(sessionId);
    const deviceReplacement = useDeviceReplacement(sessionId);
    const deviceInfo = useDeviceInfoStore((state) => state.sessions[sessionId]);
    const { requestDeviceInfo } = useRequestDeviceInfo(logger, gaiaPayloadProps);

    const isDeviceActivationGuideSupported = isGuideSupported(sessionId, DEVICE_ACTIVATION_GUIDE);
    const gaiaDevice = useMemo(() => selectGaiaGuideDevice(deviceInfo), [deviceInfo]);

    // Probe on entering the activation flow, not on mount: GAIA reads the device off the transcript, and
    // at mount the customer usually hasn't said it yet. Not skipped when a device is already stored — an
    // incidental capture answers a different question and may be the handset being replaced.
    // Marked only on a confirmed send, so a send that bailed retries on re-entry.
    useEffect(() => {
        if (currentGuideName !== DEVICE_ACTIVATION_GUIDE || hasProbedSession(sessionId)) return;

        if (requestDeviceInfo(sessionId)) {
            markSessionProbed(sessionId);
        }
    }, [currentGuideName, requestDeviceInfo, sessionId]);

    useEffect(() => {
        if (!isDeviceActivationGuideSupported) return;
        if (!deviceReplacement && !gaiaDevice) return;

        const { eSim, imei, iccid, make: claimMake, model: claimModel } = deviceReplacement ?? {};
        // Per-field so a partial GAIA result falls back for the missing half rather than blanking it.
        const make = gaiaDevice?.make ?? claimMake;
        const model = gaiaDevice?.model ?? claimModel;
        const updatedSessionMetadata = { eSim, imei, iccid, make, model };

        if (
            sessionMetadata.imei === updatedSessionMetadata.imei &&
            sessionMetadata.iccid === updatedSessionMetadata.iccid &&
            sessionMetadata.make === updatedSessionMetadata.make &&
            sessionMetadata.model === updatedSessionMetadata.model
        )
            return;

        logger.info(
            {
                deviceSource: gaiaDevice ? gaiaDevice.source : "claim",
                deviceOrigin: gaiaDevice ? deviceInfo?.origin : undefined,
                hasClaimDevice: !!(claimMake ?? claimModel),
            },
            "Solve Guide | Setting device info in session metadata",
        );
        useGuideStore.getState().setSessionMetadata(sessionId, updatedSessionMetadata);

        // refresh the metadata in the logic builder backend by updating moreTicketInfo
        sendMetadata({ metadata: updatedSessionMetadata });

        // Coverage + attribution signal, emitted LAST so instrumentation can never prevent the functional
        // path above. Fires inside the changed-metadata guard, so it emits when the resolved device changes
        // rather than per render — count DISTINCT SessionId for coverage, since a probe overriding an
        // incidental device legitimately emits twice.
        void getRootDispatcher()
            .withIdentities({ SessionId: sessionId })
            .dispatchBusinessEvent("Guide_DeviceResolved", {
                deviceSource: gaiaDevice ? gaiaDevice.source : "claim",
                deviceOrigin: gaiaDevice ? (deviceInfo?.origin ?? "claim") : "claim",
                deviceMake: make,
                deviceModel: model,
                hasClaimDevice: !!(claimMake ?? claimModel),
                agreesWithClaim: compareWithClaim(
                    [make, model].filter(Boolean).join(" "),
                    [claimMake, claimModel].filter(Boolean).join(" "),
                ),
            });
    }, [
        deviceInfo,
        deviceReplacement,
        gaiaDevice,
        isDeviceActivationGuideSupported,
        sessionId,
        sessionMetadata,
        logger,
    ]);

    useEffect(() => {
        if (!isDeviceActivationGuideSupported) return;

        const make = gaiaDevice?.make ?? deviceReplacement?.make;
        const model = gaiaDevice?.model ?? deviceReplacement?.model;
        const deviceActivationSupport = resolveDeviceActivationSupport({ make, model }, supportedDevices);

        if (deviceActivationSupport === "unknown") return;

        logger.info(
            { deviceActivationSupport, deviceSource: gaiaDevice ? gaiaDevice.source : "claim" },
            "Solve Guide | Resolved device activation support",
        );

        const guideStore = useGuideStore.getState();
        const guideSession = guideStore.getGuideSession(sessionId);
        let autoRouted = false;
        if (
            deviceActivationSupport === "unsupported" &&
            unsupportedStepId &&
            !guideSession.isWaitingForNextStep &&
            !guideSession.navigatedUnsupported
        ) {
            guideStore.setNavigatedUnsupported(sessionId, true);
            logger.info({ unsupportedStepId }, "Solve Guide | Auto-routing to unsupported device step");
            triggerNextStep(unsupportedStepId, logger, () => guideStore.setIsWaitingForNextStep(sessionId, true));
            autoRouted = true;
        }

        void getRootDispatcher()
            .withIdentities({ SessionId: sessionId })
            .dispatchBusinessEvent("Guide_DeviceSupportResolved", {
                deviceActivationSupport,
                deviceSource: gaiaDevice ? gaiaDevice.source : "claim",
                deviceMake: make,
                deviceModel: model,
                autoRouted,
            });
    }, [
        unsupportedStepId,
        gaiaDevice,
        deviceReplacement,
        supportedDevices,
        isDeviceActivationGuideSupported,
        sessionId,
        logger,
    ]);
};
